import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  RefreshCw,
  Mail,
  Phone,
  Building2,
  Clock,
  Ticket,
  MessageSquare,
  Tag,
  Pencil,
  MoreVertical,
  UserCircle,
  Calendar,
  BarChart3,
  Copy,
  ArrowRightLeft,
  AlertCircle,
  AlertTriangle,
  Star,
  CheckCircle,
  Trash2,
  Ban,
  UserCheck,
  GitMerge,
} from 'lucide-react';
import { Button, Badge, Spinner, Modal, useToast } from '@centraoneplus/ui';
import { customerService, organizationService } from '@services/customer.service';
import { ticketService, type CreateTicketDto } from '@services/ticket.service';
import { csatService } from '@services/csat.service';
import { NewTicketModal } from '@components/tickets/NewTicketModal';
import { customerEditSchema, toFieldErrors } from '@/lib/validation';
import { COUNTRY_CODE_REQUIRED_MESSAGE, parsePhone, phoneToSave } from '@/lib/phone';
import { CountryCodeSelect } from '@components/CountryCodeSelect';
import { HistoryTimeline } from '@components/history';
import type { HistoryEntry } from '@components/history';
import { useAuthStore } from '@stores/authStore';
import { canDeleteCustomer, canEditCustomer } from './permissions';
import { ManagedByCrmNotice } from '@features/integrations/huhoka/ManagedByCrmNotice';
import { ExternalRecordLink, externalLinkLabel } from '@components/common/ExternalRecordLink';
import { ProfileHeader, type ProfileSignals, type ProfileThin } from './ProfileHeader';
import { ProfileOverview } from './ProfileOverview';
import { MergeDialog } from './MergeDialog';
import { findMergeCandidates, type MergeCandidate } from './mergeCandidates';
import { initialsFor } from './CustomerAvatar';
import { domainOf, isDisposableDomain } from './disposableDomains';
import { ThemedSelect } from '@components/common/ThemedSelect';

// Types for customer 360 view
interface Customer {
  id: string;
  tenantId: string;
  email: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  avatar?: string;
  status: 'active' | 'inactive' | 'blocked' | 'pending' | 'merged';
  /** Record metadata. For a merged-away record it carries `mergedInto` (the
   *  surviving primary's id) and `mergedAt`. */
  metadata?: Record<string, unknown>;
  source: string;
  tier?: string;
  organizationId?: string;
  externalId?: string;
  /** Set while a linked CRM (HuHoKa) is the master: name, email, phone, organization and status are edited there. */
  managedBy?: string | null;
  /** BFF-supplied deep link to the record in that CRM, while the link is active. */
  externalEditUrl?: string | null;
  /** The customer's page in the external system that sent it. */
  externalUrl?: string | null;
  externalUrlLabel?: string | null;
  tags: string[];
  customFields?: Record<string, unknown>;
  preferences?: {
    language?: string;
    timezone?: string;
    emailNotifications?: boolean;
    smsNotifications?: boolean;
    marketingOptIn?: boolean;
    preferredChannel?: string;
  };
  timezone?: string;
  locale?: string;
  lastSeenAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface Organization {
  id: string;
  name: string;
  domain?: string;
  industry?: string;
  size?: string;
  website?: string;
  phone?: string;
  customerCount?: number;
}

interface Ticket {
  id: string;
  number: string;
  subject: string;
  status: 'new' | 'in_progress' | 'pending' | 'resolved';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  createdAt: string;
  resolvedAt?: string;
  closedAt?: string;
  /** Drives the OPEN TICKETS row's SLA countdown (11c §4.5). */
  resolutionDueAt?: string;
  firstResponseDueAt?: string;
  firstResponseAt?: string;
  /** 'billing', 'sso' … — what makes "her third billing ticket" sayable. */
  category?: string;
}

interface Activity {
  id: string;
  type: string;
  description: string;
  metadata?: Record<string, unknown>;
  channel?: string;
  performedBy?: string;
  performedByType?: 'customer' | 'agent' | 'system';
  createdAt: string;
}

interface Segment {
  id: string;
  name: string;
  description?: string;
  color?: string;
}

interface CustomerMetrics {
  totalTickets: number;
  openTickets: number;
  avgResolutionTime?: number;
  avgResponseTime?: number;
  satisfactionScore?: number;
  totalInteractions: number;
  /** Of those, the ones that were actual contact rather than bookkeeping. */
  contactInteractions?: number;
  lastContactAt?: string;
  customerSince: string;
  lifetimeValue?: number;
  // 11c §4.3 — every figure on the signal row carries its qualifier, so the
  // qualifiers are computed alongside the figures rather than invented at
  // render time.
  /** Open tickets past a due time. Separate from openTickets: a single count
   *  cannot say "2 open, 1 of them breaching". */
  openBreaching?: number;
  /** '1 breaching in 41m' / '1 breaching, overdue'. Null when none are. */
  breachingInLabel?: string | null;
  /** '4 billing · 3 SSO · 4 other'. Null when there is no mix worth stating. */
  ticketMix?: string | null;
  /** How many ratings the satisfaction average is drawn from — 4.6 from 2
   *  ratings is a different number from 4.6 from 40. */
  ratingCount?: number;
}

/**
 * Whether an activity row represents somebody actually being in touch.
 *
 * `customer_created`, `profile_updated`, `tag_added`, `status_changed` and the
 * rest are bookkeeping the system writes about the record; a chat, a call, an
 * email or a ticket is contact. 11d's "never contacted" turns on this
 * distinction — count bookkeeping and every record looks contacted.
 */
function isContactActivity(type: string): boolean {
  return /chat|call|email|message|conversation|ticket|sms|whatsapp|voice|reply/i.test(type);
}

/** 'chat_widget' / 'externalId' → 'Chat widget' / 'External id'. */
function titleCaseWords(s: string): string {
  return s
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}

/** '1d 4h' · '9h 42m' · '14m'. Takes hours, as the metrics carry them. */
function formatHours(hours: number): string {
  if (!Number.isFinite(hours) || hours < 0) return '—';
  const mins = Math.round(hours * 60);
  if (mins < 60) return `${mins}m`;
  const d = Math.floor(mins / 1440);
  const h = Math.floor((mins % 1440) / 60);
  const m = mins % 60;
  if (d > 0) return h ? `${d}d ${h}h` : `${d}d`;
  return m ? `${h}h ${m}m` : `${h}h`;
}

/** '2y 6m' — how long they have been a customer, beside the date they became one. */
function tenureFrom(iso: string): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '';
  const months = Math.max(
    0,
    (new Date().getFullYear() - then.getFullYear()) * 12 + (new Date().getMonth() - then.getMonth()),
  );
  const y = Math.floor(months / 12);
  const m = months % 12;
  if (y === 0) return m <= 1 ? 'new this month' : `${m}m`;
  return m ? `${y}y ${m}m` : `${y}y`;
}

/**
 * '21:32 IST' in the customer's own timezone — the first thing an agent decides
 * is whether to call now. Null when the record carries no timezone, because a
 * clock showing the AGENT's time under a "LOCAL TIME" label is worse than no
 * clock at all.
 */
function localTimeFor(timezone?: string | null): string | null {
  if (!timezone) return null;
  try {
    return new Date().toLocaleTimeString(undefined, {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    });
  } catch {
    return null; // an unrecognised zone is not a clock
  }
}

// Helper functions
function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateString);
}

function getTierBadgeColor(tier?: string): string {
  switch (tier) {
    case 'enterprise':
      return 'bg-purple-100 text-purple-800';
    case 'premium':
      return 'bg-amber-100 text-amber-800';
    case 'vip':
      return 'bg-rose-100 text-rose-800';
    case 'basic':
      return 'bg-blue-100 text-blue-800';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
  }
}

function getTicketStatusColor(status: string): string {
  switch (status) {
    case 'new':
      return 'bg-blue-100 text-blue-800';
    case 'in_progress':
      return 'bg-purple-100 text-purple-800';
    case 'pending':
      return 'bg-orange-100 text-orange-800';
    case 'resolved':
      return 'bg-green-100 text-green-800';
    case 'closed':
      return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
  }
}

function getTicketPriorityColor(priority: string): string {
  switch (priority) {
    case 'urgent':
      return 'text-red-600';
    case 'high':
      return 'text-orange-600';
    case 'medium':
      return 'text-yellow-600';
    case 'low':
      return 'text-gray-500 dark:text-gray-400';
    default:
      return 'text-gray-500 dark:text-gray-400';
  }
}

function getActivityIcon(type: string): React.ReactNode {
  switch (type) {
    case 'ticket_created':
    case 'ticket_updated':
    case 'ticket_resolved':
    case 'ticket_reopened':
      return <Ticket className="h-4 w-4" />;
    case 'message_sent':
    case 'message_received':
      return <MessageSquare className="h-4 w-4" />;
    case 'rating_submitted':
      return <Star className="h-4 w-4 text-amber-500 fill-amber-500" />;
    case 'login':
    case 'logout':
      return <UserCircle className="h-4 w-4" />;
    case 'profile_updated':
      return <Pencil className="h-4 w-4" />;
    case 'email_opened':
    case 'email_clicked':
      return <Mail className="h-4 w-4" />;
    default:
      return <Clock className="h-4 w-4" />;
  }
}

// Sub-components
// CustomerHeader and MetricsCards were deleted with phase 23. The identity
// band and signal row in ProfileHeader replace them, and PLAN.md §9 deletes
// the four-statistic block outright: "N/A" four times over said nothing about
// a record, which is the whole argument of 11d.

// RecentTickets, ActivityTimeline, CustomerDetails and CustomerSegments were
// deleted with phase 23 §4.5. ProfileOverview replaces all four: the first two
// as the left column, the last two as the right rail, on the same data. The
// rail keeps SEGMENTS even though the artboard has no such section — it is a
// real feature of this screen and a repaint is no reason to lose it.

interface Note {
  id: string;
  content: string;
  createdBy?: string;
  createdAt: string;
}

function CustomerNotes({
  notes,
  onAddNote,
  isLoading,
}: {
  notes: Note[];
  onAddNote: (content: string) => Promise<void>;
  isLoading?: boolean;
}) {
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleAddNote = async () => {
    if (!newNoteContent.trim()) return;
    setIsSaving(true);
    try {
      await onAddNote(newNoteContent);
      setNewNoteContent('');
      setIsAddingNote(false);
    } catch (error) {
      console.error('Failed to add note:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="overflow-hidden rounded-[14px] border border-[var(--co-line)] bg-[var(--co-raised)] shadow-[var(--co-shadow-card)]">
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between dark:border-gray-700">
        <h3 className="font-medium text-gray-900 dark:text-gray-100">Notes</h3>
        <Button variant="ghost" size="sm" onClick={() => setIsAddingNote(true)}>
          <Pencil className="h-4 w-4 mr-1" />
          Add note
        </Button>
      </div>
      {isAddingNote && (
        <div className="p-4 border-b border-gray-100 bg-gray-50 dark:bg-gray-900 dark:border-gray-800">
          <textarea
            value={newNoteContent}
            onChange={(e) => setNewNoteContent(e.target.value)}
            placeholder="Enter your note..."
            className="w-full px-3 py-2 border border-gray-300 rounded-[10px] text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500 dark:border-gray-700"
            rows={3}
          />
          <div className="flex justify-end gap-2 mt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setIsAddingNote(false);
                setNewNoteContent('');
              }}
            >
              Cancel
            </Button>
            <Button size="sm" onClick={handleAddNote} disabled={isSaving || !newNoteContent.trim()}>
              {isSaving ? 'Saving...' : 'Save Note'}
            </Button>
          </div>
        </div>
      )}
      <div className="divide-y divide-gray-100 dark:divide-gray-800">
        {isLoading ? (
          <div className="p-4 text-center">
            <Spinner size="sm" />
          </div>
        ) : notes.length === 0 ? (
          <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">No notes yet</div>
        ) : (
          notes.map((note) => (
            <div key={note.id} className="p-4">
              <p className="text-sm text-gray-900 dark:text-gray-100">{note.content}</p>
              <p className="text-xs text-gray-500 mt-2 dark:text-gray-400">
                {note.createdBy || 'Unknown'} • {formatRelativeTime(note.createdAt)}
              </p>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// Edit Customer Modal Component
function EditCustomerModal({
  customer,
  isOpen,
  onClose,
  onSave,
}: {
  customer: Customer;
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Partial<Customer>) => Promise<void>;
}) {
  const [formData, setFormData] = useState({
    name: customer.name || '',
    email: customer.email,
    phone: parsePhone(customer.phone).national,
    timezone: customer.timezone || '',
    locale: customer.locale || '',
  });
  const [countryCode, setCountryCode] = useState(parsePhone(customer.phone).countryCode);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Which identifiers (email / phone) the edited values collide with on ANOTHER
  // existing customer. Both are enforced unique by the backend, so a collision
  // blocks Save (mirrors the Add-customer form).
  const [duplicateReasons, setDuplicateReasons] = useState<string[]>([]);
  // Synced from HuHoKa CRM: name, email and phone are read-only here.
  const managed = !!customer.managedBy;

  // Debounced check: does the edited email or phone already belong to a
  // different (non-merged) customer? Runs only while the modal is open. The
  // search API does substring OR-matching, so results are re-filtered to exact
  // matches (email case-insensitive; phone on the last 10 digits) and the
  // current customer is excluded.
  const emailValue = formData.email;
  const phoneValue = formData.phone;
  useEffect(() => {
    if (!isOpen || managed) {
      setDuplicateReasons([]);
      return;
    }
    const normEmail = (emailValue || '').trim().toLowerCase();
    const normPhone = (phoneValue || '').replace(/\D/g, '');

    const checks: Array<{ reason: string; value: string; limit?: number; match: (c: any) => boolean }> = [];
    if (normEmail.includes('@') && normEmail !== (customer.email || '').trim().toLowerCase()) {
      checks.push({ reason: 'email', value: normEmail, match: (c) => (c.email || '').trim().toLowerCase() === normEmail });
    }
    if (normPhone.length === 10) {
      // Search by the LAST 4 digits (not the full number): the list API does a
      // raw substring match, so a stored value with separators ("555-123-4567")
      // would never contain the full "5551234567" and would not be returned. The
      // trailing 4 survive common grouping; the exact last-10 match filters hits.
      checks.push({
        reason: 'phone',
        value: normPhone.slice(-4),
        limit: 50,
        match: (c) => (c.phone || '').replace(/\D/g, '').slice(-10) === normPhone,
      });
    }

    if (checks.length === 0) {
      setDuplicateReasons([]);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const results = await Promise.all(
          checks.map((c) => customerService.getCustomers({ search: c.value, limit: c.limit ?? 5 })),
        );
        if (cancelled) return;
        const reasons = new Set<string>();
        checks.forEach((check, i) => {
          for (const c of results[i].data) {
            if (c.id === customer.id || c.status === 'merged') continue;
            if (check.match(c)) reasons.add(check.reason);
          }
        });
        setDuplicateReasons(Array.from(reasons));
      } catch {
        // Advisory check — never block Save on a failed lookup.
        if (!cancelled) setDuplicateReasons([]);
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isOpen, managed, emailValue, phoneValue, customer.id, customer.email]);

  const hasBlockingDuplicate = duplicateReasons.length > 0;
  const duplicateLabel = duplicateReasons
    .map((r) => (r === 'phone' ? 'phone number' : r))
    .join(' and ');
  const duplicateMessage = hasBlockingDuplicate
    ? `Another customer already uses this ${duplicateLabel}. Please use a unique email or phone number.`
    : '';

  // Re-seed the form with the customer's stored values every time the modal
  // opens. Without this, the component stays mounted after Cancel (it only
  // returns null) so edited-but-discarded values would persist and reappear on
  // reopen instead of the actual data present in the system.
  useEffect(() => {
    if (!isOpen) return;
    const parsed = parsePhone(customer.phone);
    setFormData({
      name: customer.name || '',
      email: customer.email,
      phone: parsed.national,
      timezone: customer.timezone || '',
      locale: customer.locale || '',
    });
    setCountryCode(parsed.countryCode);
    setFieldErrors({});
    setError(null);
  }, [isOpen, customer]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Keep the trailing 10 digits so a pasted number with a country/trunk prefix
    // (e.g. "+1 555 123 4567") resolves to the correct national number and the
    // duplicate check below matches the stored record.
    const digits = e.target.value.replace(/\D/g, '').slice(-10);
    setFormData((prev) => ({ ...prev, phone: digits }));
    setFieldErrors((prev) => ({ ...prev, phone: '' }));
  };

  // True only when at least one field differs from the original customer values
  // (mirrors the initial state above). Used to disable Save when nothing changed.
  const originalPhone = parsePhone(customer.phone);
  const isDirty =
    formData.name !== (customer.name || '') ||
    formData.email !== customer.email ||
    formData.phone !== originalPhone.national ||
    countryCode !== originalPhone.countryCode ||
    formData.timezone !== (customer.timezone || '') ||
    formData.locale !== (customer.locale || '');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (managed) {
      // Only the fields CentraOne Plus owns; the CRM owns name, email and phone.
      setIsSaving(true);
      try {
        await onSave({ timezone: formData.timezone, locale: formData.locale });
        onClose();
      } catch (err: any) {
        setError(err.message || 'Failed to update customer');
      } finally {
        setIsSaving(false);
      }
      return;
    }

    const result = customerEditSchema.safeParse(formData);
    if (!result.success) {
      setFieldErrors(toFieldErrors(result.error));
      return;
    }
    // An old number with no code can stay as it is while other fields are
    // edited; a code is needed only once the number itself changes.
    if (formData.phone !== originalPhone.national && !countryCode) {
      setFieldErrors({ phone: COUNTRY_CODE_REQUIRED_MESSAGE });
      return;
    }
    setFieldErrors({});

    if (hasBlockingDuplicate) {
      setError(duplicateMessage);
      return;
    }

    setIsSaving(true);
    try {
      // Store the phone with its country code, e.g. "+1 5551234567".
      await onSave({ ...formData, phone: phoneToSave(customer.phone, countryCode, formData.phone) });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to update customer');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Edit Customer
        </h2>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}

        {!error && hasBlockingDuplicate && (
          <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <p className="text-sm text-amber-700 dark:text-amber-300">{duplicateMessage}</p>
          </div>
        )}

        <ManagedByCrmNotice
          managedBy={customer.managedBy}
          editUrl={customer.externalEditUrl}
          fields="Name, email and phone"
        />

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              readOnly={managed}
              disabled={managed}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            {fieldErrors.name && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              readOnly={managed}
              disabled={managed}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            {fieldErrors.email && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.email}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Phone <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <CountryCodeSelect value={countryCode} onChange={managed ? () => undefined : setCountryCode} />
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handlePhoneChange}
                readOnly={managed}
                disabled={managed}
                inputMode="numeric"
                maxLength={10}
                placeholder="5551234567"
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            {fieldErrors.phone && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.phone}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Timezone
            </label>
            <ThemedSelect
              name="timezone"
              value={formData.timezone}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">Select timezone...</option>
              <option value="America/New_York">Eastern Time (ET)</option>
              <option value="America/Chicago">Central Time (CT)</option>
              <option value="America/Denver">Mountain Time (MT)</option>
              <option value="America/Los_Angeles">Pacific Time (PT)</option>
              <option value="Europe/London">London (GMT)</option>
              <option value="Europe/Paris">Paris (CET)</option>
              <option value="Asia/Tokyo">Tokyo (JST)</option>
            </ThemedSelect>
            {fieldErrors.timezone && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldErrors.timezone}</p>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving || !isDirty || hasBlockingDuplicate}
              title={hasBlockingDuplicate ? duplicateMessage : undefined}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Lightweight name-similarity check (mirrors the backend intent but is NOT
// gated by email domain): exact match, or ≥50% of name tokens shared.
// FindDuplicatesModal and MergeCustomerModal were deleted with phase 23 §6.
// MergeDialog replaces both: finding duplicates and resolving them were two
// screens, which is how a duplicate stays visible and unactionable. Their
// matcher lives on verbatim in mergeCandidates.ts, reasons included — those
// reasons ARE the evidence 11e renders.

// Main component
export default function CustomerProfilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuthStore();
  // Deleting a customer is admin-only; the BFF enforces the same rule.
  const canDelete = canDeleteCustomer(user?.role);
  // Editing a customer is admin-only too; the BFF enforces the same rule.
  const canEdit = canEditCustomer(user?.role);

  const [isLoading, setIsLoading] = useState(true);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [metrics, setMetrics] = useState<CustomerMetrics | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  // -1 = not counted yet. 11d's panel omits the domain clause rather than
  // claiming "shared by 0 other records" before the count has come back.
  const [domainSharedBy, setDomainSharedBy] = useState(-1);
  const [duplicateCount, setDuplicateCount] = useState(0);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [notes, setNotes] = useState<Note[]>([]);
  const [isNotesLoading, setIsNotesLoading] = useState(false);
  // 11e — one dialog does both finding and resolving.
  const [isMergeOpen, setIsMergeOpen] = useState(false);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  const [mergeCandidates, setMergeCandidates] = useState<MergeCandidate[]>([]);
  const [isCreateTicketModalOpen, setIsCreateTicketModalOpen] = useState(false);
  const [isCreatingTicket, setIsCreatingTicket] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  // #18931 — the surviving primary this record was merged into (for the banner
  // link). Only set when this record's status is 'merged'.
  const [mergedInto, setMergedInto] = useState<{ id: string; name: string } | null>(null);
  // #20610 — the surviving primary was deleted, so the audit record points to a
  // record that no longer exists. Drives a "no longer exists" banner instead of
  // a dead "View primary" link.
  const [primaryMissing, setPrimaryMissing] = useState(false);

  const loadCustomerData = useCallback(async () => {
    if (!id) return;

    setIsLoading(true);
    try {
      // Fetch customer data from API
      const customerData = await customerService.getCustomer(id);

      // Map API response to component's Customer type
      const mappedCustomer: Customer = {
        id: customerData.id,
        tenantId: customerData.tenantId,
        email: customerData.email,
        name: customerData.name,
        phone: customerData.phone,
        avatar: customerData.avatarUrl,
        // The customer-detail read is uncached end-to-end (BFF findById, the
        // BackendApiService, and the customer-service all skip caching), so the
        // backend row is authoritative — use its status directly. (A previous
        // router-state "trusted status" override re-seeded to the pre-block
        // value on hard refresh, which made a blocked customer read as active.)
        status: customerData.status as Customer['status'],
        metadata: customerData.metadata,
        source: customerData.source || 'unknown',
        tier: undefined,
        organizationId: customerData.organizationId,
        externalId: customerData.externalId,
        tags: customerData.tags || [],
        customFields: customerData.customFields,
        preferences: {
          timezone: customerData.timezone,
          language: customerData.locale,
        },
        timezone: customerData.timezone,
        locale: customerData.locale,
        lastSeenAt: customerData.lastSeenAt,
        createdAt: customerData.createdAt,
        updatedAt: customerData.updatedAt,
      };

      setCustomer(mappedCustomer);

      // #18931 / #20610 — if this record was merged away, resolve the surviving
      // primary so the "Merged into …" banner can link to it by name. If the
      // primary was since deleted (404), never link to a missing record — show
      // a "no longer exists" banner instead.
      if (mappedCustomer.status === 'merged') {
        const primaryId = mappedCustomer.metadata?.mergedInto as string | undefined;
        if (primaryId) {
          try {
            const primary = await customerService.getCustomer(primaryId);
            setMergedInto({ id: primary.id, name: primary.name || primary.email });
            setPrimaryMissing(false);
          } catch (err: any) {
            // 404 → the primary was deleted; any other error → unknown, but
            // still don't offer a link we can't trust.
            setMergedInto(null);
            setPrimaryMissing(err?.response?.status === 404);
          }
        } else {
          setMergedInto(null);
          setPrimaryMissing(false);
        }
      } else {
        setMergedInto(null);
        setPrimaryMissing(false);
      }

      // Fetch organization if customer has one
      if (customerData.organizationId) {
        try {
          const orgData = await organizationService.getOrganization(customerData.organizationId);
          setOrganization({
            id: orgData.id,
            name: orgData.name,
            domain: orgData.domain,
            industry: orgData.industry,
            size: orgData.size,
            website: orgData.website,
            phone: orgData.phone,
            customerCount: orgData.customerCount,
          });
        } catch (err) {
          console.error('Failed to fetch organization:', err);
          setOrganization(null);
        }
      } else if (customerData.organization) {
        // Use embedded organization data if available
        setOrganization({
          id: customerData.organization.id,
          name: customerData.organization.name,
        });
      } else {
        setOrganization(null);
      }

      // Fetch customer timeline/activities and notes
      let activityCount = 0;
      // Contact only — see isContactActivity. Drives the 11d thin variant.
      let contactCount = 0;
      try {
        const timelineData = await customerService.getTimeline(id, { limit: 50 });
        const items = timelineData.items || [];

        // Separate activities and notes
        const activityItems: Activity[] = [];
        const noteItems: Note[] = [];

        items.forEach((item) => {
          if (item.category === 'note') {
            noteItems.push({
              id: item.id,
              content: item.description || '',
              // Note authors are resolved to a display name server-side and
              // returned as actorName (the byline falls back to "Unknown").
              createdBy: item.actorName || undefined,
              createdAt: item.occurredAt || item.createdAt,
            });
          } else {
            activityItems.push({
              id: item.id,
              type: item.type,
              description: item.description || '',
              metadata: item.metadata,
              performedBy: item.actorName || undefined,
              performedByType: (item.actorType as Activity['performedByType']) || undefined,
              createdAt: item.occurredAt || item.createdAt,
            });
          }
        });

        setActivities(activityItems.slice(0, 10));
        setNotes(noteItems);
        activityCount = timelineData.total || activityItems.length;
        // 11d's cohort is "no ticket, chat or call" — CONTACT, not bookkeeping.
        // Every record has "Customer created" and a few "Profile updated"
        // rows, so counting all activity would mean no record is ever thin and
        // the 11d variant would never render at all.
        contactCount = activityItems.filter((a) => isContactActivity(a.type)).length;
      } catch (err) {
        console.error('Failed to fetch timeline:', err);
        setActivities([]);
        setNotes([]);
      }

      // Fetch customer segments
      try {
        const segmentsData = await customerService.getCustomerSegments(id);
        const mappedSegments: Segment[] = segmentsData.map((seg) => ({
          id: seg.id,
          name: seg.name,
          description: seg.description,
          color: seg.color,
        }));
        setSegments(mappedSegments);
      } catch (err) {
        console.error('Failed to fetch segments:', err);
        setSegments([]);
      }

      // Fetch customer tickets
      let ticketCount = 0;
      let openTicketCount = 0;
      let avgResolutionTime: number | undefined;
      let openBreaching = 0;
      let breachingInLabel: string | null = null;
      let ticketMix: string | null = null;
      try {
        const ticketsResponse = await ticketService.getTickets({
          customerId: id,
          ...(customerData.email ? { contactEmail: customerData.email } : {}),
          limit: 10,
        });
        const mappedTickets: Ticket[] = (ticketsResponse.data || []).map((t: any) => ({
          id: t.id,
          number: t.ticketNumber || t.number || t.id.slice(0, 8).toUpperCase(),
          subject: t.subject,
          status: t.status as 'new' | 'in_progress' | 'pending' | 'resolved',
          priority: t.priority as 'low' | 'medium' | 'high' | 'urgent',
          createdAt: t.createdAt,
          resolvedAt: t.resolvedAt,
          closedAt: t.closedAt,
          resolutionDueAt: t.resolutionDueAt,
          firstResponseDueAt: t.firstResponseDueAt,
          firstResponseAt: t.firstResponseAt,
          category: String(t.ticketType?.name ?? t.category ?? '').trim().toLowerCase() || undefined,
        }));
        setTickets(mappedTickets);
        ticketCount = ticketsResponse.total || mappedTickets.length;
        const openRaw = (ticketsResponse.data || []).filter((t: any) =>
          ['new', 'in_progress', 'pending'].includes(t.status),
        );
        openTicketCount = openRaw.length;

        // Breaching = an open ticket past its resolution due time, or past its
        // first-response due time with no first response. The same two
        // dimensions the SLA screen reports, so the profile and Reports cannot
        // disagree about what "breaching" means.
        const now = Date.now();
        const dueTimes: number[] = [];
        openBreaching = openRaw.filter((t: any) => {
          const resDue = t.resolutionDueAt ? new Date(t.resolutionDueAt).getTime() : null;
          const frDue = t.firstResponseDueAt ? new Date(t.firstResponseDueAt).getTime() : null;
          const resBreached = resDue !== null && resDue < now;
          const frBreached = frDue !== null && frDue < now && !t.firstResponseAt;
          // The soonest deadline still ahead of us is what the label counts down to.
          for (const d of [resDue, frDue]) if (d !== null && d > now) dueTimes.push(d);
          return resBreached || frBreached;
        }).length;

        // Something already overdue outranks something merely approaching.
        if (openBreaching > 0) {
          breachingInLabel = `${openBreaching} breaching, overdue`;
        } else if (dueTimes.length > 0) {
          const soonest = Math.min(...dueTimes);
          const mins = Math.round((soonest - now) / 60000);
          breachingInLabel =
            mins < 60
              ? `1 due in ${mins}m`
              : mins < 1440
                ? `1 due in ${Math.round(mins / 60)}h`
                : `1 due in ${Math.round(mins / 1440)}d`;
        }

        // '4 billing · 3 SSO · 4 other'. Built from whatever the tickets
        // actually carry; when nothing groups, the qualifier stays null rather
        // than printing a mix of one.
        const groups = new Map<string, number>();
        for (const raw of ticketsResponse.data || []) {
          const t = raw as any;
          const key = String(t.ticketType?.name ?? t.category ?? '').trim().toLowerCase();
          if (!key) continue;
          groups.set(key, (groups.get(key) ?? 0) + 1);
        }
        if (groups.size > 1) {
          const sorted = [...groups.entries()].sort((a, b) => b[1] - a[1]);
          const head = sorted.slice(0, 3);
          const tail = sorted.slice(3).reduce((n, [, v]) => n + v, 0);
          ticketMix = [...head.map(([k, v]) => `${v} ${k}`), ...(tail ? [`${tail} other`] : [])].join(' · ');
        }

        // Average resolution time (hours) across this customer's resolved tickets.
        // Fall back to closedAt when resolvedAt is missing (tickets closed without
        // an explicit "resolved" transition never get a resolvedAt timestamp).
        const resolutionHours = mappedTickets
          .map((t) => {
            const end = t.resolvedAt || t.closedAt;
            if (!end) return null;
            return (new Date(end).getTime() - new Date(t.createdAt).getTime()) / 3_600_000;
          })
          .filter((h): h is number => h !== null && Number.isFinite(h) && h >= 0);
        if (resolutionHours.length > 0) {
          const avg = resolutionHours.reduce((sum, h) => sum + h, 0) / resolutionHours.length;
          avgResolutionTime = Math.round(avg * 10) / 10;
        }
      } catch (err) {
        console.error('Failed to fetch customer tickets:', err);
        setTickets([]);
      }

      // Average CSAT (satisfaction) across this customer's completed surveys.
      let satisfactionScore: number | undefined;
      let ratingCount = 0;
      try {
        const csat = await csatService.getResponses({ customerId: id, limit: 200 });
        const ratings = (csat.data || [])
          .map((r) => r.rating)
          .filter((n): n is number => typeof n === 'number' && n > 0);
        ratingCount = ratings.length;
        if (ratings.length > 0) {
          const avg = ratings.reduce((sum, n) => sum + n, 0) / ratings.length;
          satisfactionScore = Math.round(avg * 10) / 10;
        }
      } catch (err) {
        console.error('Failed to fetch customer CSAT responses:', err);
      }

      // Set metrics from customer data
      setMetrics({
        totalTickets: ticketCount,
        openTickets: openTicketCount,
        totalInteractions: activityCount,
        contactInteractions: contactCount,
        customerSince: customerData.createdAt,
        lastContactAt: customerData.lastSeenAt,
        avgResolutionTime,
        satisfactionScore,
        openBreaching,
        breachingInLabel,
        ticketMix,
        ratingCount,
      });

      // 11d — how many OTHER live records share this address's domain. It is
      // the number that turns one dead record into a cleanup task, so the
      // panel waits for it rather than claiming a domain is shared before it
      // has counted. Uses the register's own search, which matches on email.
      const domain = domainOf(customerData.email);
      if (domain && ticketCount === 0 && contactCount === 0) {
        try {
          const siblings = await customerService.getCustomers({ search: `@${domain}`, limit: 1 });
          setDomainSharedBy(Math.max(0, (siblings.total ?? 0) - 1));
        } catch {
          // Leave it at -1: the panel then omits the domain clause entirely
          // rather than saying "shared by 0 other records" on a failed count.
          setDomainSharedBy(-1);
        }
      }

      // Duplicates are a fact about this record's identity, so the breadcrumb
      // states the count before anything below it is trusted.
      try {
        const groups = await customerService.findDuplicates();
        const mine = (groups || []).find((g: any[]) => g.some((c) => c.id === id));
        setDuplicateCount(mine ? mine.length - 1 : 0);
      } catch {
        setDuplicateCount(0);
      }

    } catch (err) {
      console.error('Failed to fetch customer:', err);
      setCustomer(null);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadCustomerData();
  }, [loadCustomerData]);

  // ── 11c / 11d ────────────────────────────────────────────────────────────
  // A thin record is a record with NOTHING in it — no ticket, no conversation.
  // That single condition picks the variant, exactly as `signals === null`
  // does in the design kit; nothing else may decide it.
  // "No ticket, chat or call" — bookkeeping rows do not count, or every record
  // would look contacted and 11d would never render.
  const isThinRecord =
    !!metrics && metrics.totalTickets === 0 && (metrics.contactInteractions ?? 0) === 0;

  const profileSignals = useMemo<ProfileSignals | null>(() => {
    if (!metrics || isThinRecord) return null;
    const since = new Date(metrics.customerSince);
    return {
      openNow: metrics.openTickets,
      openBreaching: metrics.openBreaching ?? 0,
      breachingInLabel: metrics.breachingInLabel ?? null,
      lifetimeTickets: metrics.totalTickets,
      ticketMix: metrics.ticketMix ?? null,
      avgResolution:
        metrics.avgResolutionTime === undefined ? null : formatHours(metrics.avgResolutionTime),
      // The team median is not available on this route, so the comparison is
      // omitted rather than guessed — "2× your median" against an unknown
      // median is the kind of qualifier that is worse than none.
      avgResolutionNote: null,
      satisfaction: metrics.satisfactionScore ?? null,
      ratingCount: metrics.ratingCount ?? 0,
      sinceLabel: Number.isNaN(since.getTime())
        ? '—'
        : since.toLocaleDateString(undefined, { month: 'short', year: 'numeric' }),
      tenureLabel: tenureFrom(metrics.customerSince),
    };
  }, [metrics, isThinRecord]);

  const profileThin = useMemo<ProfileThin | null>(() => {
    if (!customer || !metrics || !isThinRecord) return null;
    const domain = domainOf(customer.email);
    const created = new Date(customer.createdAt);
    const ageDays = Number.isNaN(created.getTime())
      ? 0
      : Math.max(0, Math.floor((Date.now() - created.getTime()) / 86_400_000));
    return {
      addedLabel: `ADDED ${
        Number.isNaN(created.getTime())
          ? '—'
          : created.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }).toUpperCase()
      } · ${(customer.source || 'manual entry').toUpperCase()}`,
      recordAgeDays: ageDays,
      domain,
      domainIsDisposable: isDisposableDomain(domain),
      domainSharedBy,
    };
  }, [customer, metrics, isThinRecord, domainSharedBy]);

  // §4.5 — the panel says only what the record can prove.
  //
  // The artboard's last clause ("the underlying seat-count discrepancy has not
  // been fixed at source") is a human judgement no query produces, so it is not
  // attempted. What IS derivable is the breach and the repeat, and those are
  // the two facts that make the panel worth reading. With neither, the panel
  // does not render — a template with visible holes is worse than no panel.
  const attention = useMemo(() => {
    if (!customer || !metrics) return null;
    const now = Date.now();
    const openTickets = tickets.filter((t) => !['resolved', 'closed', 'merged'].includes(t.status));

    const clauses: string[] = [];

    // The soonest deadline among open tickets, overdue or approaching.
    const due = openTickets
      .map((t) => ({ t, at: t.resolutionDueAt ? new Date(t.resolutionDueAt).getTime() : NaN }))
      .filter((x) => Number.isFinite(x.at))
      .sort((a, b) => a.at - b.at)[0];
    if (due) {
      const mins = Math.round((due.at - now) / 60000);
      const span = (n: number) =>
        n < 60 ? `${n} minutes` : n < 1440 ? `${Math.round(n / 60)} hours` : `${Math.round(n / 1440)} days`;
      clauses.push(
        mins < 0
          ? `#${due.t.number} is ${span(-mins)} past its resolution SLA`
          : `#${due.t.number} breaches SLA in ${span(mins)}`,
      );
    }

    // A repeat on ONE cause is a different problem from unrelated tickets, and
    // no list of tickets says so.
    const byCategory = new Map<string, number>();
    for (const t of tickets) {
      if (!t.category) continue;
      byCategory.set(t.category, (byCategory.get(t.category) ?? 0) + 1);
    }
    const repeat = [...byCategory.entries()].sort((a, b) => b[1] - a[1])[0];
    if (repeat && repeat[1] >= 3) {
      clauses.push(`this is their ${repeat[1]}th ${repeat[0]} ticket`);
    }

    if (clauses.length === 0) return null;
    return {
      headline: 'NEEDS ATTENTION',
      body: `${clauses.join(' — and ').replace(/^./, (c) => c.toUpperCase())}.`,
    };
  }, [customer, metrics, tickets]);

  // The right rail: facts about the record itself. Each row appears only when
  // the record carries it, so the rail never pads itself with em dashes.
  const recordRail = useMemo(() => {
    if (!customer) return [];
    const rows: { label: string; value: string }[] = [];
    if (customer.source) rows.push({ label: 'Source', value: titleCaseWords(customer.source) });
    if (customer.externalId) rows.push({ label: 'External ID', value: customer.externalId });
    if (customer.timezone) rows.push({ label: 'Timezone', value: customer.timezone });
    if (customer.locale) rows.push({ label: 'Locale', value: customer.locale });
    rows.push({
      label: 'Last seen',
      value: customer.lastSeenAt ? formatRelativeTime(customer.lastSeenAt) : 'never',
    });
    return rows;
  }, [customer]);

  const preferenceRail = useMemo(() => {
    const p = customer?.preferences;
    if (!p) return [];
    const rows: { label: string; value: string }[] = [];
    if (p.preferredChannel) {
      rows.push({ label: 'Preferred channel', value: titleCaseWords(p.preferredChannel) });
    }
    if (p.marketingOptIn !== undefined) {
      rows.push({ label: 'Marketing', value: p.marketingOptIn ? 'Opted in' : 'Opted out' });
    }
    return rows;
  }, [customer]);

  const customFieldRail = useMemo(() => {
    const cf = customer?.customFields;
    if (!cf) return [];
    return Object.entries(cf)
      .filter(([, v]) => v !== null && v !== undefined && v !== '')
      .slice(0, 8)
      .map(([k, v]) => ({ label: titleCaseWords(k), value: String(v) }));
  }, [customer]);

  const handleBack = () => {
    navigate('/customers');
  };

  const handleEdit = () => {
    // Belt-and-suspenders: the Edit button is hidden for non-admins, and the
    // BFF rejects the update — never open the edit modal for a non-admin.
    if (!canEdit) return;
    // A merged-away record is a read-only audit record — never editable.
    if (customer?.status === 'merged') return;
    setIsEditModalOpen(true);
  };

  const handleSaveCustomer = async (data: Partial<Customer>) => {
    if (!id) return;
    await customerService.updateCustomer(id, {
      name: data.name,
      email: data.email,
      phone: data.phone,
      timezone: data.timezone,
      locale: data.locale,
    });
    // Reload customer data after saving
    await loadCustomerData();
    toast.success('Customer updated');
  };

  const handleRefresh = () => {
    loadCustomerData();
  };

  const handleAddNote = async (content: string) => {
    if (!id) return;
    try {
      await customerService.addNote(id, content);
      // Reload to get the new note
      await loadCustomerData();
      toast.success('Note added');
    } catch (err) {
      console.error('Failed to add note:', err);
      toast.error(
        'Failed to add note',
        (err as any)?.response?.data?.message ||
          (err as any)?.response?.data?.error?.message ||
          (err as any)?.message ||
          'Please try again.',
      );
      throw err;
    }
  };

  // 11e. Both entry points — the breadcrumb's duplicate button and the header's
  // Merge action — open the same dialog, because finding duplicates and
  // resolving them are one task.
  const openMerge = async () => {
    if (!customer) return;
    // A merged-away record cannot itself be a merge source/target again.
    if (customer.status === 'merged') return;
    setIsMergeOpen(true);
    setMergeLoading(true);
    try {
      setMergeCandidates(await findMergeCandidates(customer));
    } catch (err: any) {
      setMergeCandidates([]);
      toast.error('Could not look for duplicates', err?.message || 'Please try again.');
    } finally {
      setMergeLoading(false);
    }
  };

  const closeMerge = () => {
    if (isMerging) return;
    setIsMergeOpen(false);
    setMergeCandidates([]);
  };

  /**
   * The backend merge is PAIRWISE (one source into one target), so N records
   * merge as N-1 sequential calls. Sequential, not parallel: each call runs its
   * own transaction against the same target, and firing them together races on
   * the target's tag array. `allSettled` so one failure does not hide the rest.
   */
  const handleMergeConfirm = async (primaryId: string, mergedIds: string[], note: string) => {
    setIsMerging(true);
    const failures: string[] = [];
    let movedTickets = 0;
    try {
      for (const sourceId of mergedIds) {
        try {
          const result: any = await customerService.mergeCustomers(primaryId, sourceId, note);
          movedTickets += result?.mergedTickets ?? 0;
        } catch (err: any) {
          failures.push(
            err?.response?.data?.message || err?.message || 'one record could not be merged',
          );
        }
      }

      const succeeded = mergedIds.length - failures.length;
      if (succeeded > 0) {
        toast.success(
          `${succeeded} record${succeeded === 1 ? '' : 's'} merged`,
          movedTickets > 0
            ? `${movedTickets} ticket${movedTickets === 1 ? '' : 's'} moved onto the primary.`
            : 'Notes, activities and tags moved onto the primary.',
        );
      }
      if (failures.length > 0) {
        toast.error(
          `${failures.length} of ${mergedIds.length} could not be merged`,
          failures[0],
        );
      }

      setIsMergeOpen(false);
      setMergeCandidates([]);
      // The primary may not be the record we were looking at. If it isn't, this
      // record no longer exists as a live customer, so go to the survivor
      // rather than leaving the user on a merged-away page.
      if (primaryId !== id) navigate(`/customers/${primaryId}`);
      else loadCustomerData();
    } finally {
      setIsMerging(false);
    }
  };

  const handleCreateTicket = () => {
    if (customer?.status === 'blocked') {
      toast.error('Customer is blocked', 'Unblock the customer before creating a ticket.');
      return;
    }
    if (customer?.status === 'merged') {
      toast.error('Customer was merged', 'This record was merged into another. Open the primary record to create a ticket.');
      return;
    }
    setIsCreateTicketModalOpen(true);
  };

  const handleToggleBlock = async () => {
    if (!id || !customer) return;
    // A merged-away record is a read-only audit record — block/unblock does not apply.
    if (customer.status === 'merged') return;
    const newStatus = customer.status === 'blocked' ? 'active' : 'blocked';
    try {
      await customerService.updateCustomer(id, { status: newStatus } as any);
      // The PATCH succeeded, so `newStatus` is authoritative — optimistically
      // set exactly what we requested for instant feedback. Do NOT trust the
      // response body's `status`: a write-through cache can echo the pre-update
      // record, and because the stale value is truthy a `updated?.status ||
      // newStatus` fallback would never kick in, leaving the banner/badge stuck
      // on the old status. A subsequent reload reads the (authoritative,
      // uncached) backend status, which now matches.
      setCustomer((prev) => (prev ? { ...prev, status: newStatus } : prev));
      toast.success(
        newStatus === 'blocked' ? 'Customer blocked' : 'Customer unblocked',
        newStatus === 'blocked'
          ? 'Their features and ticket creation are now disabled.'
          : 'Their access has been restored.',
      );
    } catch (err: any) {
      console.error('Failed to update customer status:', err);
      toast.error(
        'Failed to update status',
        err?.response?.data?.message || err?.message || 'Please try again.',
      );
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    try {
      await customerService.deleteCustomer(id);
      navigate('/customers');
    } catch (err) {
      console.error('Failed to delete customer:', err);
      // Surface the reason (e.g. the backend's 409 when the customer still has
      // active/open tickets) instead of failing silently.
      const message =
        (err as any)?.response?.data?.message ||
        (err as any)?.message ||
        'Failed to delete customer.';
      toast.error('Could not delete customer', message);
    }
  };

  const handleSubmitTicket = async (dto: CreateTicketDto) => {
    setIsCreatingTicket(true);
    try {
      await ticketService.createTicket({
        ...dto,
        customerId: id,
      });
      setIsCreateTicketModalOpen(false);
      // Reload tickets to show the new one
      await loadCustomerData();
      toast.success('Ticket created');
    } catch (err) {
      console.error('Failed to create ticket:', err);
      toast.error(
        'Failed to create ticket',
        (err as any)?.response?.data?.message ||
          (err as any)?.response?.data?.error?.message ||
          (err as any)?.message ||
          'Please try again.',
      );
      throw err;
    } finally {
      setIsCreatingTicket(false);
    }
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
        <UserCircle className="h-16 w-16 text-gray-300 mb-4" />
        <p className="text-lg font-medium mb-2">Customer not found</p>
        <Button variant="outline" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Customers
        </Button>
      </div>
    );
  }

  // #18931 — a merged-away record is a read-only audit record. It shows a
  // "Merged into …" banner and every mutating action (Edit, Merge, Delete,
  // Block, new ticket) is disabled.
  const isMerged = customer.status === 'merged';

  return (
    // Floating cards on the canvas, matching the ticket pages.
    <div className="h-full flex flex-col gap-[14px] bg-[var(--co-canvas)] pb-4 pr-4 pt-4">
      {/* 11c / 11d. One component: `signals === null` picks the thin variant,
          and that is the only thing that decides it. Everything below the tab
          bar is unchanged — the tabs, the overview panels, notes, activity,
          block/unblock, merge and delete all behave exactly as before. */}
      <ProfileHeader
        name={customer.name || customer.email}
        email={customer.email}
        phone={customer.phone ?? null}
        avatarInitials={initialsFor(customer.name, customer.email)}
        status={customer.status}
        tags={customer.tags ?? []}
        organisation={organization ? { id: organization.id, name: organization.name } : null}
        localTime={localTimeFor(customer.timezone)}
        timezone={customer.timezone ?? null}
        locale={customer.locale ?? null}
        duplicateCount={duplicateCount}
        signals={profileSignals}
        thin={profileThin}
        activeTab={activeTab}
        tabCounts={{
          tickets: metrics?.totalTickets ?? tickets.length,
          history: activities.length,
          notes: notes.length,
        }}
        onTab={setActiveTab}
        onBack={handleBack}
        onOpenOrganisation={(orgId) => navigate(`/organizations/${orgId}`)}
        onFindDuplicates={openMerge}
        onEdit={handleEdit}
        onStartChat={() => navigate('/live-chat')}
        onNewTicket={handleCreateTicket}
        onAssignOrganisation={handleEdit}
        onReviewSimilar={(domain) => navigate(`/customers?search=${encodeURIComponent(`@${domain}`)}`)}
        canEdit={canEdit && !isMerged}
        blocked={customer.status === 'blocked'}
        merged={isMerged}
        actions={
          <>
            <ExternalRecordLink
              url={customer.externalUrl}
              label={externalLinkLabel(customer)}
              className="!h-[34px]"
            />
            <button
              type="button"
              onClick={handleRefresh}
              aria-label="Refresh"
              className="flex h-[34px] w-[34px] items-center justify-center rounded-[10px] border border-[var(--co-line-strong)] bg-[var(--co-raised)] text-[var(--co-ink-3)] hover:border-[var(--co-brass)] hover:bg-[var(--co-brass-wash)]"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={openMerge}
              disabled={customer.status === 'blocked' || isMerged}
              className="flex h-[34px] items-center rounded-[10px] border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 text-[12.5px] text-[var(--co-ink-2)] hover:border-[var(--co-brass)] hover:bg-[var(--co-brass-wash)] disabled:cursor-not-allowed disabled:text-[var(--co-ink-4)]"
            >
              Merge
            </button>
            {canDelete && !isMerged && (
              <button
                type="button"
                onClick={() => setIsDeleteConfirmOpen(true)}
                className="flex h-[34px] items-center rounded-[10px] border px-3 text-[12.5px] hover:bg-[var(--co-burgundy-wash)]"
                style={{ borderColor: 'var(--co-burgundy-line)', color: 'var(--co-burgundy)' }}
              >
                Delete
              </button>
            )}
            <button
              type="button"
              onClick={handleToggleBlock}
              disabled={isMerged}
              className="flex h-[34px] items-center rounded-[10px] border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 text-[12.5px] text-[var(--co-ink-2)] hover:border-[var(--co-brass)] hover:bg-[var(--co-brass-wash)] disabled:cursor-not-allowed disabled:text-[var(--co-ink-4)]"
            >
              {customer.status === 'blocked' ? 'Unblock' : 'Block'}
            </button>
          </>
        }
      />

      {/* Merged banner — this record was merged into a primary and is now a
          read-only audit record. Links to the surviving primary. (#18931) */}
      {isMerged && (
        <div className="flex-shrink-0 flex items-center gap-3 rounded-[14px] px-5 py-3 bg-blue-50 border border-blue-200 shadow-[var(--co-shadow-card)] dark:bg-blue-900/20 dark:border-blue-800">
          <GitMerge className="h-5 w-5 text-blue-600 flex-shrink-0" />
          <p className="text-sm text-blue-700 dark:text-blue-300 flex-1">
            This record was <strong>merged</strong> into{' '}
            {mergedInto ? (
              <Link
                to={`/customers/${mergedInto.id}`}
                className="font-semibold underline underline-offset-2 hover:text-blue-900 dark:hover:text-blue-100"
              >
                {mergedInto.name}
              </Link>
            ) : primaryMissing ? (
              // #20610 — the primary was deleted; never link to a missing record.
              <strong>a record that no longer exists</strong>
            ) : (
              'another customer'
            )}
            . It is kept as an audit record — editing, merge, block, delete and ticket creation are disabled.
          </p>
          {mergedInto && (
            <Button variant="outline" size="sm" onClick={() => navigate(`/customers/${mergedInto.id}`)}>
              View primary
            </Button>
          )}
        </div>
      )}

      {/* Blocked banner — features are disabled while blocked */}
      {customer.status === 'blocked' && (
        <div className="flex-shrink-0 flex items-center gap-3 rounded-[14px] px-5 py-3 bg-red-50 border border-red-200 shadow-[var(--co-shadow-card)] dark:bg-red-900/20 dark:border-red-800">
          <Ban className="h-5 w-5 text-red-600 flex-shrink-0" />
          <p className="text-sm text-red-700 dark:text-red-300 flex-1">
            This customer is <strong>blocked</strong>. Editing, ticket creation, and other actions are disabled. Unblock the customer to restore access.
          </p>
          <Button variant="outline" size="sm" onClick={handleToggleBlock}>
            <UserCheck className="h-4 w-4 mr-1" />
            Unblock
          </Button>
        </div>
      )}

      {/* The four MetricsCards are gone: 11c replaces them with the signal row
          above, where every figure carries the qualifier that makes it
          readable, and 11d replaces them with the finding. §9 deletes the
          four-statistic N/A block outright. The tab bar moved into
          ProfileHeader for the same reason — it belongs to the identity
          region, not to the body it scrolls with. */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {/* Overview owns its own scrolling: it is two columns that scroll
            independently, and the right rail has to reach the bottom of the
            viewport whatever the left column's height. So it gets a flex
            context with a definite height rather than this wrapper's block
            scroll, which would size the rail to the content beside it and
            leave the sunken ground stopping mid-page. The other tabs are
            single columns and keep the scroll they had. */}
        <div
          className={
            activeTab === 'overview'
              ? 'flex min-h-0 flex-1 flex-col'
              : 'scrollbar-hide flex-1 overflow-y-auto'
          }
        >
          {activeTab === 'overview' && (
            <ProfileOverview
              attention={attention}
              tickets={tickets}
              totalTickets={metrics?.totalTickets ?? tickets.length}
              activities={activities}
              notes={notes}
              record={recordRail}
              preferences={preferenceRail}
              customFields={customFieldRail}
              tags={customer.tags ?? []}
              segments={segments}
              onOpenTicket={(ticketId) => navigate(`/tickets/${ticketId}`)}
              onAllTickets={() => setActiveTab('tickets')}
              onAddTag={handleEdit}
            />
          )}

          {activeTab === 'tickets' && (
            <div>
              <div className="overflow-hidden rounded-[14px] border border-[var(--co-line)] bg-[var(--co-raised)] shadow-[var(--co-shadow-card)]">
                <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between dark:border-gray-700">
                  <h3 className="font-medium text-gray-900 dark:text-gray-100">All Tickets ({tickets.length})</h3>
                  <Button size="sm" onClick={handleCreateTicket} disabled={customer.status === 'blocked' || isMerged}>
                    Create Ticket
                  </Button>
                </div>
                <div className="divide-y divide-gray-100 dark:divide-gray-800">
                  {tickets.map((ticket) => (
                    <Link
                      key={ticket.id}
                      to={`/tickets/${ticket.id}`}
                      className="block p-4 hover:bg-[var(--co-brass-wash)] transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-primary-600">
                              #{ticket.number}
                            </span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getTicketStatusColor(ticket.status)}`}>
                              {ticket.status}
                            </span>
                            <span className={`text-xs font-medium ${getTicketPriorityColor(ticket.priority)}`}>
                              {ticket.priority}
                            </span>
                          </div>
                          <p className="text-sm text-gray-900 mt-1 dark:text-gray-100">{ticket.subject}</p>
                        </div>
                        <div className="text-right text-xs text-gray-500 dark:text-gray-400">
                          <p>Created {formatRelativeTime(ticket.createdAt)}</p>
                          {ticket.resolvedAt && (
                            <p className="text-green-600">Resolved {formatRelativeTime(ticket.resolvedAt)}</p>
                          )}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <div>
              <div className="overflow-hidden rounded-[14px] border border-[var(--co-line)] bg-[var(--co-raised)] shadow-[var(--co-shadow-card)]">
                <HistoryTimeline
                  entries={activities.map((a): HistoryEntry => ({
                    id: a.id,
                    eventType: a.type,
                    title: a.description || a.type,
                    actorName: a.performedBy,
                    actorType: a.performedByType || 'system',
                    metadata: a.metadata || {},
                    occurredAt: a.createdAt,
                    createdAt: a.createdAt,
                  }))}
                  onTicketClick={(ticketId) => navigate(`/tickets/${ticketId}`)}
                  emptyMessage="No history recorded for this customer yet"
                />
              </div>
            </div>
          )}

          {activeTab === 'notes' && (
            <div>
              <CustomerNotes
                notes={notes}
                onAddNote={handleAddNote}
                isLoading={isNotesLoading}
              />
            </div>
          )}
        </div>
      </div>

      {/* Edit Customer Modal */}
      {customer && (
        <EditCustomerModal
          customer={customer}
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSave={handleSaveCustomer}
        />
      )}

      {/* 11e — one dialog. Finding duplicates and resolving them were two
          screens before, which is how a duplicate stays visible and
          unactionable: you could see the matches in one and had to go
          somewhere else to do anything about them. */}
      {isMergeOpen && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={closeMerge} />
          <div className="relative">
            {mergeLoading ? (
              <div className="w-[860px] max-w-[94vw] rounded-[14px] bg-[var(--co-raised)] p-10 text-center font-mono text-[11px] tracking-[.14em] text-[var(--co-ink-4)]">
                LOOKING FOR DUPLICATES…
              </div>
            ) : mergeCandidates.length === 0 ? (
              <div className="w-[520px] max-w-[94vw] rounded-[14px] bg-[var(--co-raised)] p-8">
                <div className="font-mono text-[9.5px] font-semibold tracking-[.2em] text-[var(--co-ink-4)]">
                  NO DUPLICATES FOUND
                </div>
                <p className="mt-3 text-[13px] leading-[1.55] text-[var(--co-ink-2)]">
                  No other record shares this customer&rsquo;s phone number, email address or a
                  similar name. Nothing to merge.
                </p>
                <div className="mt-5 flex justify-end">
                  <button
                    type="button"
                    onClick={closeMerge}
                    className="flex h-[34px] items-center rounded-[10px] border border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 text-[12.5px] text-[var(--co-ink-2)] hover:border-[var(--co-ink)]"
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <MergeDialog
                candidates={mergeCandidates}
                busy={isMerging}
                onCancel={closeMerge}
                onMerge={handleMergeConfirm}
              />
            )}
          </div>
        </div>,
        document.body,
      )}

      {/* Create Ticket Modal */}
      <NewTicketModal
        isOpen={isCreateTicketModalOpen}
        onClose={() => setIsCreateTicketModalOpen(false)}
        onSubmit={handleSubmitTicket}
        isLoading={isCreatingTicket}
        initialData={{
          customerId: id,
          contactEmail: customer?.email,
          contactName: customer?.name,
        }}
      />

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)} title="Delete Customer">
        <div className="p-4">
          {metrics && metrics.openTickets > 0 ? (
            <div className="mb-4 flex items-start gap-3 rounded-lg border border-red-300 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/30">
              <AlertCircle className="h-5 w-5 flex-shrink-0 text-red-600 dark:text-red-400 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-800 dark:text-red-300">
                  Can’t delete this customer
                </p>
                <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                  <strong>{customer?.name || customer?.email}</strong> has {metrics.openTickets} active/open
                  ticket{metrics.openTickets === 1 ? '' : 's'}. Resolve or close
                  {metrics.openTickets === 1 ? ' it' : ' them'} first, then try again.
                </p>
              </div>
            </div>
          ) : (
            <div className="mb-4 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/30">
              <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                  Delete customer?
                </p>
                <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                  Are you sure you want to delete <strong>{customer?.name || customer?.email}</strong>? This action cannot be undone.
                </p>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setIsDeleteConfirmOpen(false)}>
              {metrics && metrics.openTickets > 0 ? 'Close' : 'Cancel'}
            </Button>
            {!(metrics && metrics.openTickets > 0) && (
              <Button
                variant="danger"
                size="sm"
                onClick={() => { setIsDeleteConfirmOpen(false); handleDelete(); }}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}
